<script setup lang="ts">
import {
  type CSSProperties,
  computed,
  onBeforeUnmount,
  onMounted,
  shallowRef,
  useTemplateRef,
} from 'vue';
import { useLegacyGalleryTelemetry } from '@/components/legacy-gallery/useLegacyGalleryTelemetry';
import {
  artStyleGridColumnStart,
  artStyleGridLayout,
} from './artStyleGridLayout';
import type {
  ArtStyleClickEvent,
  ArtStyleGalleryItem,
  ArtStyleGridClass,
} from './artStyleTypes';

const props = defineProps<{
  gridClass: ArtStyleGridClass;
  items: readonly ArtStyleGalleryItem[];
  locked: boolean;
  previewAspectRatio: number;
  vertical: boolean;
}>();

const emit = defineEmits<{
  select: [event: ArtStyleClickEvent, item: ArtStyleGalleryItem];
}>();

const root = useTemplateRef<HTMLElement>('root');
const measurements = shallowRef({
  width: 1,
  height: 1,
  gap: 12,
  labelHeight: 48,
});
const layout = computed(() =>
  artStyleGridLayout(
    props.items.length,
    measurements.value.width,
    measurements.value.height,
    props.previewAspectRatio,
    measurements.value.gap,
    measurements.value.labelHeight
  )
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const gridStyle = computed(() => ({
  '--art-style-grid-columns': layout.value.columns * 2,
  '--art-style-grid-image-height': `${layout.value.imageHeight}px`,
  '--art-style-grid-image-width': `${layout.value.imageWidth}px`,
}));
const telemetry = useLegacyGalleryTelemetry({
  actualItemsPerSlide: () => props.items.length,
  isGridMode: true,
  items: () => props.items,
  selectedItemUuids: () =>
    props.items.filter((item) => item.selected).map((item) => item.uuid),
  selectionMode: null,
});

function measure(): void {
  const element = root.value;
  if (!element || element.clientWidth === 0 || element.clientHeight === 0) {
    return;
  }
  const style = getComputedStyle(element);
  measurements.value = {
    width:
      element.clientWidth -
      Number.parseFloat(style.paddingLeft) -
      Number.parseFloat(style.paddingRight),
    height:
      element.clientHeight -
      Number.parseFloat(style.paddingTop) -
      Number.parseFloat(style.paddingBottom),
    gap: Number.parseFloat(style.rowGap),
    labelHeight: Number.parseFloat(style.fontSize) * 3.25,
  };
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function itemStyle(index: number): CSSProperties {
  return {
    gridColumnStart: artStyleGridColumnStart(
      index,
      props.items.length,
      layout.value.columns
    ),
  };
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function select(event: MouseEvent, item: ArtStyleGalleryItem): void {
  if (!props.locked && telemetry.recordItemClick(event, item)) {
    emit('select', props.vertical ? null : event, item);
  }
}

let observer: ResizeObserver | undefined;
onMounted(() => {
  measure();
  observer = new ResizeObserver(measure);
  if (root.value) {
    observer.observe(root.value);
  }
});
onBeforeUnmount(() => observer?.disconnect());
</script>

<template>
  <div ref="root" class="ArtStyleGrid" data-testid="art-style-grid">
    <div
      class="ArtStyleGrid__items"
      :class="[
        vertical ? ['ArtStyle__custom-grid', gridClass] : 'ArtStyle__gallery',
        { 'pointer-events-none': locked },
      ]"
      :style="gridStyle"
      :data-columns="layout.columns"
    >
      <button
        v-for="(item, index) in items"
        :key="item.uuid"
        type="button"
        class="ArtStyleGrid__item"
        :class="{
          'ArtStyleGrid__item--selected': item.selected,
          'ArtStyle__custom-item--selected': vertical && item.selected,
          'ArtStyle__gallery-item--selected': !vertical && item.selected,
        }"
        :data-testid="vertical ? 'art-style-custom-item' : 'gallery-item'"
        :data-style-id="item.id"
        :aria-pressed="item.selected"
        :disabled="locked"
        :style="itemStyle(index)"
        @click="select($event, item)"
      >
        <img
          class="ArtStyleGrid__image"
          :class="vertical ? 'ArtStyle__custom-image' : 'ArtStyle__gallery-image'"
          :src="item.url"
          alt=""
          @error="telemetry.recordImageLoadError(item, index, $event)"
        />
        <span
          class="ArtStyleGrid__title"
          :class="{
            'ArtStyle__custom-title': vertical,
            'ArtStyle__gallery-item-title': !vertical,
            'ArtStyle__custom-title--selected': vertical && item.selected,
            'ArtStyle__gallery-item-title--selected': !vertical && item.selected,
          }"
        >{{ item.title }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ArtStyleGrid {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  padding: 8px;
  gap: clamp(12px, 1.5vw, 32px);
  font-size: clamp(14px, var(--kiosk-choice-card-font-size, 1rem), 32px);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
}

.ArtStyleGrid__items {
  display: grid;
  grid-template-columns: repeat(var(--art-style-grid-columns), minmax(0, 1fr));
  gap: inherit;
  flex: none;
  align-self: center;
  width: 100%;
  margin-block: auto;
}

.ArtStyleGrid__item {
  grid-column: auto / span 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6em;
  min-width: 0;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-shadow: inherit;
  cursor: pointer;

  &:disabled { cursor: default; }
  &:focus-visible {
    outline: 3px solid currentColor;
    outline-offset: 4px;
    border-radius: 1rem;
  }
}

.ArtStyleGrid__image {
  display: block;
  width: var(--art-style-grid-image-width);
  max-width: 100%;
  height: var(--art-style-grid-image-height);
  object-fit: contain;
  border-radius: 1rem;
}

.ArtStyleGrid__title {
  min-height: 2.5em;
  max-width: 100%;
  line-height: 1.25;
  text-align: center;
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  overflow-wrap: anywhere;
}

.ArtStyleGrid__item--selected {
  .ArtStyleGrid__image { box-shadow: 0 0 0 4px #6496ff; }
  .ArtStyleGrid__title { color: #6496ff; }
}
</style>
